// 2026-08-31 - FormuLearning - Step 4 §4.3 Sample Test Result 패널 (이슈 #58 S5 2차/#68)
// 2026-11-03 수정 - 이슈 #114: viewer(조회 전용)는 Y 실측 입력·Save Results 비활성.
// 파일 위치: frontend/src/features/step4/TestResultPanel.tsx
// 목적: 성분표에서 처방을 선택하면 그 처방의 결과 입력창이 아래에 열린다(`M84-63`).
//       헤더에 **지금 입력 중인 배치**를 명시하고(`M84-11`), Y 실측값(Hardness 등)의
//       **화면 입력 경로**를 연다(`M84-12`). 종전에는 엑셀 업로드로만 들어왔다.
//       정본 = UI/UX v2.2 §5.9 `COMP-TEST-RESULT-PANEL` · §4.7 §4.3.
//
// f2는 여기에 입력 칸을 두지 않는다 — 용출 프로파일에서 산출되는 시스템 계산값(R1)이라
// `y_values`에 저장하지 않는다(인터페이스 §0.2 원칙 2). 왜 못 고치는지가 보여야 하므로
// 읽기 전용으로는 보인다.
//
// 2026-08-31 수정 - 이슈 #58 S5 5차(#68, M84-75): **`⬇ Template (.xlsx)` 폐지**
//                   (`M84-63` 개정). 화면 입력 경로가 2차(직접 입력)·4차(붙여넣기)로
//                   서면서 양식을 받아 채워 올릴 이유가 사라졌다(사용자 판단 260831).
//                   백엔드 양식 생성 엔드포인트는 남는다.
// 2026-08-31 수정 - 이슈 #58 S5 4차(#68): **붙여넣기 grid**(`M84-72`, 정본 UI/UX v2.2 §5.9
//                   `COMP-PASTE-GRID` 적용 대상 2곳 중 하나). 여기는 값 N개가 한 줄로 늘어선
//                   1차원 형상이라 가로 1×N 복사도 세로 N×1 복사도 같은 뜻으로 받는다
//                   (`applyLinear` — 행 우선 평탄화).
// 2026-09-24 수정 - 이슈 #76(G5, M901-32): 미저장 편집 여부를 `onDirtyChange` 로 알린다(Next Optimization 잠금).

import { useEffect, useRef, useState } from 'react'
import type { ClipboardEvent } from 'react'
import type { LotBatch } from '../../api/lot'
import type { YVariable } from '../../api/step3'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'
import {
  applyLinear, describePaste, isRangePaste, parseClipboardMatrix, stripHeaderRows,
} from '../../lib/pasteGrid'

interface Props {
  batch: LotBatch
  /** Step 3 §3.3 Y 변수 정의. 입력 칸은 이 목록에서 f2를 뺀 것이 된다. */
  yVariables: YVariable[]
  onSave: (yValues: Record<string, number | null>) => Promise<void>
  busy: boolean
  /** 미저장 편집 여부 알림(#76, M901-32). 언마운트 시 false 로 한 번 더 알린다. */
  onDirtyChange?: (dirty: boolean) => void
}

/** f2 자리 판정 — 백엔드 `is_f2_variable()`과 같은 규약(`f2*` 접두어). */
const isF2 = (name: string) => name.trim().toLowerCase().startsWith('f2')

/** 입력 대상 응답명 — f2와 이름이 빈 행(미작성 Y 행)은 뺀다. */
function inputNames(yVariables: YVariable[]): string[] {
  const names = yVariables.map((v) => (v.response ?? '').trim()).filter((n) => n && !isF2(n))
  return [...new Set(names)]
}

/** 서버 값 → 입력 문자열. null·undefined는 빈 칸이며 0과 구분된다. */
const toDraft = (batch: LotBatch, names: string[]): Record<string, string> =>
  Object.fromEntries(names.map((n) => {
    const v = batch.y_values?.[n]
    return [n, typeof v === 'number' && Number.isFinite(v) ? String(v) : '']
  }))

export default function TestResultPanel({
  batch, yVariables, onSave, busy, onDirtyChange,
}: Props) {
  const canWrite = useCanWrite()
  const names = inputNames(yVariables)
  const [draft, setDraft] = useState<Record<string, string>>(() => toDraft(batch, names))
  const [dirty, setDirty] = useState(false)
  // 미저장 편집 알림 — 콜백은 ref 로 든다(DissolutionGrid 와 같은 규약).
  const dirtyCb = useRef(onDirtyChange)
  useEffect(() => { dirtyCb.current = onDirtyChange })
  useEffect(() => { dirtyCb.current?.(dirty) }, [dirty])
  useEffect(() => () => { dirtyCb.current?.(false) }, [])
  const [saving, setSaving] = useState(false)
  const [pasteNote, setPasteNote] = useState<string | null>(null)

  // 붙여넣기(`M84-72`) — 앵커 입력부터 순서대로 채운다. 셀마다 setDraft 를 부르면
  // stale closure 로 마지막 값만 남으므로 복사본에 모두 채운 뒤 한 번에 넣는다.
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>, anchorIndex: number) => {
    const text = e.clipboardData.getData('text/plain')
    if (!isRangePaste(text)) return // 단일 셀은 기본 동작에 맡긴다
    e.preventDefault()

    const matrix = stripHeaderRows(parseClipboardMatrix(text))
    const next = { ...draft }
    const res = applyLinear({
      matrix, anchorIndex, count: names.length,
      write: (i, value) => { next[names[i]] = String(value) },
    })

    if (res.filled > 0) {
      setDraft(next)
      setDirty(true)
    }
    setPasteNote(describePaste(res, '칸'))
  }

  // 배치가 바뀌거나 서버 값이 갱신되면 그 배치의 값으로 다시 seed 한다(`M84-63` 복원).
  // 편집 중(dirty)에는 덮지 않는다 — `DissolutionGrid`가 쓰는 규약 그대로다.
  const [syncedId, setSyncedId] = useState(batch.id)
  const [syncedY, setSyncedY] = useState(batch.y_values)
  if (batch.id !== syncedId || (!dirty && batch.y_values !== syncedY)) {
    setSyncedId(batch.id)
    setSyncedY(batch.y_values)
    setDraft(toDraft(batch, names))
    setDirty(false)
  }

  const handleSave = async () => {
    // 값이 비면 null 로 보낸다 — 서버에서 **비우기**로 처리된다(0 과 다르다).
    const payload: Record<string, number | null> = {}
    for (const n of names) {
      const raw = (draft[n] ?? '').trim()
      payload[n] = raw === '' ? null : Number(raw)
    }
    setSaving(true)
    try {
      await onSave(payload)
      setDirty(false)
      setPasteNote(null)
    } finally {
      setSaving(false)
    }
  }

  const invalid = names.some((n) => {
    const raw = (draft[n] ?? '').trim()
    return raw !== '' && !Number.isFinite(Number(raw))
  })

  return (
    <div
      className="mb-2 rounded border p-2.5"
      style={{ borderColor: 'var(--border-subtle)', background: 'var(--color-bg-panel)' }}
      data-testid="test-result-panel"
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {/* M84-11 — 어느 처방에 값을 넣는지 보이지 않으면 오입력이 난다. */}
        <span className="text-[11px] font-semibold text-text-body" data-testid="trp-batch">
          Batch: <span style={{ color: 'var(--color-accent-teal)' }}>{batch.batch_code}</span>
        </span>
        {/* M84-72 — 엑셀 범위 복붙이 주 입력 경로다. 업로드는 부가 기능이다. */}
        <span
          className="text-[9px] text-text-muted"
          title="엑셀에서 값을 복사해 입력칸에 붙여넣으면 그 칸부터 순서대로 채워집니다. 가로·세로 어느 방향으로 복사해도 같습니다."
          data-testid="trp-paste-hint"
        >
          ⌘/Ctrl+V 붙여넣기 가능
        </span>
        {batch.is_excluded && (
          <span className="rounded px-1 text-[9px] text-danger" style={{ border: '1px solid var(--border-subtle)' }}>
            계산 제외 — 값은 저장되나 f2·학습에 쓰이지 않습니다
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
        {names.map((n, i) => (
          <label key={n} className="flex items-center gap-1.5 text-[11px] text-text-secondary">
            <span className="whitespace-nowrap">{n}</span>
            <input
              type="number"
              step="any"
              value={draft[n] ?? ''}
              onChange={(e) => { setDirty(true); setDraft((p) => ({ ...p, [n]: e.target.value })) }}
              onPaste={(e) => handlePaste(e, i)}
              disabled={!canWrite}
              title={canWrite ? undefined : READ_ONLY_TITLE}
              className="w-24 rounded bg-bg-input px-2 py-1 text-[11px] text-text-body outline-none"
              style={{ border: '1px solid var(--border-soft)' }}
              data-testid={`trp-input-${n}`}
              placeholder="—"
            />
          </label>
        ))}

        {/* f2는 입력 대상이 아니다 — 용출 프로파일에서 산출된다. */}
        <span className="flex items-center gap-1.5 text-[11px] text-text-secondary" data-testid="trp-f2">
          f2 (min)
          <span
            className="rounded px-2 py-1 text-[11px] font-semibold"
            style={{ background: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }}
            title="용출 프로파일에서 산출되는 시스템 계산값이라 직접 입력하지 않습니다"
          >
            {batch.f2 != null ? batch.f2.toFixed(1) : '—'}
          </span>
        </span>

        <button
          onClick={() => void handleSave()}
          disabled={saving || busy || !dirty || invalid || names.length === 0 || !canWrite}
          title={canWrite ? undefined : READ_ONLY_TITLE}
          className="ml-auto rounded bg-accent-purple px-3 py-1.5 text-[11px] font-semibold text-[#1a1b1e] transition-opacity hover:opacity-90 disabled:opacity-40"
          data-testid="trp-save"
        >
          {saving ? 'Saving...' : 'Save Results'}
        </button>
      </div>

      {pasteNote && (
        <p className="mt-1.5 text-[10px] text-accent-teal" data-testid="trp-paste-note">{pasteNote}</p>
      )}
      {names.length === 0 && (
        <p className="mt-1.5 text-[10px] text-text-muted">
          입력할 응답이 없습니다 — Step 3 §3.3에서 Y 변수를 먼저 정의하십시오(f2는 시스템 계산값이라 제외됩니다).
        </p>
      )}
      {invalid && (
        <p className="mt-1.5 text-[10px] text-warn">숫자가 아닌 값이 있습니다.</p>
      )}
    </div>
  )
}
